/** @fileoverview Converts saved and streamed chat messages for the AI SDK UI. */

import type { PreviewSelectionItem } from '../lib/previewSelection';
import { toSkillReadData } from '../lib/skillRead';
import { listMessages } from '../services/chatApi';
import { useChatStore } from '../stores/chatStore';
import type {
  AskQuestionData,
  FabrikAskQuestionPart,
  FabrikUIMessage,
  Message,
} from '../types/chat';
import type { SetActiveMessages } from './types';

const toTextUIMessages = (messages: Message[]): FabrikUIMessage[] =>
  messages.map((message) => ({
    id: message.id,
    role: message.role === 'user' || message.role === 'system' ? message.role : 'assistant',
    metadata: {
      activity: message.message.activity,
      feedback: message.assistant_message_feedback,
    },
    parts: message.message.parts.flatMap((part): FabrikUIMessage['parts'][number][] => {
      if ((part.type === 'text' || part.type === 'reasoning') && typeof part.text === 'string') {
        return [{ type: part.type, text: part.text }];
      }
      if (part.type === 'data-preview-selection' && Array.isArray(part.data)) {
        const data = part.data.filter(
          (item): item is PreviewSelectionItem =>
            typeof item?.path === 'string' && typeof item.data === 'string'
        );
        if (data.length) return [{ type: 'data-preview-selection', data }];
      }
      if (part.type === 'data-skill-read') {
        const skillRead = toSkillReadData(part.data);
        if (skillRead) return [{ type: 'data-skill-read', data: skillRead }];
      }
      if (part.type === 'data-ask-question' && part.data && typeof part.data === 'object') {
        const data = part.data as AskQuestionData;
        return [{ type: 'data-ask-question', id: data.id, data }];
      }
      if (
        'toolCallId' in part &&
        typeof part.toolCallId === 'string' &&
        'state' in part &&
        (part.type === 'dynamic-tool' || part.type.startsWith('tool-'))
      ) {
        return [part as unknown as FabrikUIMessage['parts'][number]];
      }
      return [];
    }),
  }));

/** Converts saved messages; a message's saved attachments lead its parts. */
export const toUIMessages = (messages: Message[]): FabrikUIMessage[] =>
  toTextUIMessages(messages).map((uiMessage, index) => ({
    ...uiMessage,
    parts: [
      ...(messages[index].attachments ?? []).map((attachment) => ({
        type: 'data-attachment' as const,
        data: attachment,
      })),
      ...uiMessage.parts,
    ],
  }));

/** Keeps tool-call positions from the live stream when saved messages are refreshed. */
export const preserveStreamedToolOrder = (
  savedMessages: FabrikUIMessage[],
  streamedMessages: FabrikUIMessage[]
): FabrikUIMessage[] => {
  const streamedById = new Map(streamedMessages.map((message) => [message.id, message]));
  const latestStreamedAssistant = [...streamedMessages]
    .reverse()
    .find((message) => message.role === 'assistant');
  const latestStreamedAssistantWithTools = latestStreamedAssistant?.parts.some(
    (part) => 'toolCallId' in part
  )
    ? latestStreamedAssistant
    : undefined;
  const latestSavedAssistantIndex = savedMessages.reduce(
    (latest, message, index) => (message.role === 'assistant' ? index : latest),
    -1
  );

  return savedMessages.map((message, index) => {
    const streamed =
      streamedById.get(message.id) ||
      (index === latestSavedAssistantIndex ? latestStreamedAssistantWithTools : undefined);
    if (!streamed || !streamed.parts.some((part) => 'toolCallId' in part)) return message;

    const savedQuestions = new Map(
      message.parts
        .filter((part): part is FabrikAskQuestionPart => part.type === 'data-ask-question')
        .map((part) => [part.data.id, part])
    );
    const savedContent = message.parts.filter(
      (part): part is Extract<FabrikUIMessage['parts'][number], { type: 'text' | 'reasoning' }> =>
        part.type === 'text' || part.type === 'reasoning'
    );
    const usedSavedContent = new Set<number>();
    const parts = streamed.parts.flatMap((part): FabrikUIMessage['parts'][number][] => {
      if (part.type === 'text' || part.type === 'reasoning') {
        const savedIndex = savedContent.findIndex(
          (savedPart, index) => !usedSavedContent.has(index) && savedPart.text === part.text
        );
        if (savedIndex < 0) return [];
        usedSavedContent.add(savedIndex);
        return [savedContent[savedIndex]];
      }
      if (part.type === 'data-ask-question') return [savedQuestions.get(part.data.id) || part];
      return [part];
    });
    parts.push(...savedContent.filter((_, index) => !usedSavedContent.has(index)));
    return { ...message, parts };
  });
};

/** Refreshes persisted messages only if the same conversation is still selected. */
export const refreshSavedMessages = async (
  tenantId: string,
  conversationId: string,
  setActiveMessages: SetActiveMessages,
  streamedMessages?: FabrikUIMessage[]
) => {
  const data = await listMessages(tenantId, conversationId, 0);
  if (useChatStore.getState().selected?.id !== conversationId) return false;

  const messages = [...data.assistantMessages].reverse();
  useChatStore.setState({ messages, messageTotal: data.totalCount });
  const uiMessages = toUIMessages(messages);
  setActiveMessages(
    streamedMessages ? preserveStreamedToolOrder(uiMessages, streamedMessages) : uiMessages
  );
  return true;
};
